import { ReactNode, useMemo, useState } from 'react';
import {
  Create,
  CreateButton,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  FunctionField,
  List,
  ListButton,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  Show,
  ShowButton,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  required,
  useRecordContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { DataTableListActions } from 'src/client/components/data-table-list-actions';
import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { formatCampaignTitle } from '../campaigns/CampaignList';
import { LineItemRecord, formatLineItemTitle } from '../line-items/LineItemCreate';

const specialtyGroupFilters = [
  <TextInput key="name" label="Name" source="name" alwaysOn />,
  <TextInput key="externalPioId" label="PIO ID" source="externalPioId" />,
];

const optionalTextInput = (value: string) => (value === '' ? null : value);

type SpecialtyGroupRecord = {
  id?: string;
  name?: string | null;
  externalPioId?: string | null;
};

const sectionGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
} as const;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box sx={{ '& .MuiTypography-root': { fontSize: 'body2.fontSize' } }}>{children}</Box>
  </Box>
);

const SpecialtyGroupForm = () => (
  <SimpleForm>
    <TextInput source="name" fullWidth validate={required()} />
    <TextInput source="externalPioId" label="PIO ID" fullWidth parse={optionalTextInput} />
  </SimpleForm>
);

export const SpecialtyGroupList = () => (
  <List
    actions={
      <DataTableListActions filters={specialtyGroupFilters}>
        <CreateButton />
      </DataTableListActions>
    }
    sort={{ field: 'name', order: 'ASC' }}
    perPage={25}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="name" />
      <TextField source="externalPioId" label="PIO ID" />
      <DateField source="updatedAt" showTime />
      <ShowButton />
      <EditButton />
    </Datagrid>
  </List>
);

const SpecialtyGroupShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
  </TopToolbar>
);

const SpecialtyGroupHeader = () => {
  const record = useRecordContext<SpecialtyGroupRecord>();

  return (
    <CardContent>
      <CardHeader title={record?.name ?? 'Specialty Group'} sx={{ px: 0, pt: 0, pb: 1 }} />
    </CardContent>
  );
};

const OverviewSection = () => (
  <CardContent>
    <CardHeader title="Overview" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Name">
        <TextField source="name" emptyText="-" />
      </Field>
      <Field label="PIO ID">
        <TextField source="externalPioId" emptyText="-" />
      </Field>
      <Field label="Specialty Group ID">
        <TextField source="id" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const AuditSection = () => (
  <CardContent>
    <CardHeader title="Audit" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Created">
        <ESTDateField source="createdAt" />
      </Field>
      <Field label="Updated">
        <ESTDateField source="updatedAt" />
      </Field>
    </Box>
  </CardContent>
);

const LineItemsToolbar = ({
  filterValue,
  onFilterChange,
}: {
  filterValue: string;
  onFilterChange: (value: string) => void;
}) => (
  <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
    <MuiTextField
      label="Filter line items"
      value={filterValue}
      onChange={event => onFilterChange(event.target.value)}
      size="small"
      sx={{ minWidth: 260 }}
    />
  </Box>
);

const RelatedLineItemsSection = () => {
  const [filterValue, setFilterValue] = useState('');
  const lineItemFilter = useMemo(
    () => (filterValue ? { name: { operator: 'includesInsensitive', value: filterValue } } : {}),
    [filterValue],
  );

  return (
    <CardContent>
      <CardHeader title="Line Items With This Specialty" sx={{ px: 0, pt: 0 }} />
      <LineItemsToolbar filterValue={filterValue} onFilterChange={setFilterValue} />
      <ReferenceManyField
        reference="lineItems"
        target="specialty"
        source="name"
        sort={{ field: 'startDate', order: 'DESC' }}
        perPage={25}
        filter={lineItemFilter}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
      >
        <Datagrid
          rowClick="show"
          bulkActionButtons={false}
          empty={<Box sx={{ p: 2 }}>No line items found for this specialty.</Box>}
        >
          <FunctionField
            label="Line Item"
            source="name"
            render={(record: LineItemRecord) => formatLineItemTitle(record)}
          />
          <ReferenceField source="campaignId" reference="campaigns" label="Campaign" link="show">
            <FunctionField render={formatCampaignTitle} />
          </ReferenceField>
          <ESTDateField source="startDate" label="Start Date" />
          <ESTDateField source="endDate" label="End Date" />
          <TextField source="status" label="Status" emptyText="-" />
          <TextField source="invoiceStatus" label="Invoice Status" emptyText="-" />
          <ShowButton resource="lineItems" />
          <EditButton resource="lineItems" />
        </Datagrid>
      </ReferenceManyField>
    </CardContent>
  );
};

export const SpecialtyGroupShow = () => (
  <Show actions={<SpecialtyGroupShowActions />}>
    <SpecialtyGroupHeader />
    <Divider />
    <OverviewSection />
    <Divider />
    <AuditSection />
    <Divider />
    <RelatedLineItemsSection />
  </Show>
);

export const SpecialtyGroupCreate = () => (
  <Create redirect="show">
    <SpecialtyGroupForm />
  </Create>
);

export const SpecialtyGroupEdit = () => (
  <Edit redirect="show">
    <SpecialtyGroupForm />
  </Edit>
);
